import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { ROLE_HOME } from "@/lib/nav";
import TwoFactorChallenge from "@/pages/auth/TwoFactorChallenge";

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="relative min-h-screen w-full lg:grid lg:grid-cols-2 bg-white">
      <div className="relative hidden lg:block bg-white">
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-slate-800">
          <div className="flex items-center gap-3">
            <img src="/rallypointgo-icon.png" alt="RallyPointGo" className="h-24 w-24 object-contain" />
            <div>
              <p className="text-2xl font-extrabold font-heading">RallyPoint<span className="text-orange-500">Go</span></p>
              <p className="text-xs font-semibold uppercase tracking-widest text-sky-600">Management System</p>
            </div>
          </div>
          <div>
            <h2 className="text-4xl font-extrabold leading-tight font-heading text-slate-900">Where communication meets accountability.</h2>
            <p className="mt-4 max-w-md text-slate-600">RallyPointGo is the unified hub for managing high school student programs. Keep students on track, keep parents informed, and keep administrators in the loop — while coordinating announcements, schedules, student achievements, and production readiness for contests and shows, all from one streamlined dashboard.</p>
          </div>
          <div />
        </div>
      </div>
      <div className="flex min-h-screen items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden flex items-center gap-2.5">
            <img src="/rallypointgo-icon.png" alt="RallyPointGo" className="h-20 w-20 object-contain" />
            <div>
              <p className="text-xl font-extrabold font-heading">RallyPoint<span className="text-orange-500">Go</span></p>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-600">Management System</p>
            </div>
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-heading">{title}</h1>
          {subtitle && <p className="mt-2 text-sm text-slate-500">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          <p className="mt-16 text-center text-sm text-slate-500" data-testid="copyright">© 2026 Joseph Johnson</p>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState(null); // team options when one email has multiple accounts
  const [teamId, setTeamId] = useState("");
  const [twofa, setTwofa] = useState(null); // { challengeId, enrolled }

  useEffect(() => {
    const msg = searchParams.get("session");
    if (msg) toast.message("Signed out", { description: msg });
  }, [searchParams]);

  const finishLogin = (user) => {
    toast.success("Welcome back!");
    navigate(ROLE_HOME[user.role]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (accounts && !teamId) {
      toast.error("Please choose a team to sign in to");
      return;
    }
    setLoading(true);
    try {
      const res = await login(email.trim(), password, accounts ? teamId : undefined);
      if (res.multiple) {
        setAccounts(res.accounts);
        setTeamId("");
        toast.message("Multiple team accounts found", { description: "Choose which team to sign in to." });
        return;
      }
      if (res.twofa) {
        setTwofa({ challengeId: res.challengeId, enrolled: res.enrolled });
        return;
      }
      finishLogin(res.user);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (twofa) {
    return (
      <AuthShell title="Verify it's you" subtitle="One more step to protect this account.">
        <TwoFactorChallenge
          challengeId={twofa.challengeId}
          enrolled={twofa.enrolled}
          onSuccess={finishLogin}
          onCancel={() => { setTwofa(null); setPassword(""); }}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Sign in" subtitle="Access your RallyPointGo account.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email}
            onChange={(e) => { setEmail(e.target.value); setAccounts(null); setTeamId(""); }}
            placeholder="you@example.com" data-testid="login-email-input" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs font-semibold text-sky-600 hover:underline" data-testid="forgot-password-link">Forgot?</Link>
          </div>
          <div className="relative">
            <Input id="password" type={showPassword ? "text" : "password"} required value={password}
              onChange={(e) => { setPassword(e.target.value); setAccounts(null); setTeamId(""); }}
              placeholder="••••••••" className="pr-10" data-testid="login-password-input" />
            <button type="button" onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"} data-testid="toggle-password-visibility">
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {accounts && (
          <div className="space-y-2" data-testid="login-team-picker">
            <Label htmlFor="team">Choose your team</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger id="team" data-testid="login-team-select">
                <SelectValue placeholder="Select a team account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.team_id} value={a.team_id} data-testid={`login-team-option-${a.team_id}`}>
                    {a.team_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">This email is used on more than one team. Pick the account you want to enter.</p>
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="login-submit-button"
          className="w-full rounded-full bg-slate-900 hover:bg-slate-800 transition-colors">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {accounts ? "Continue" : "Sign in"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        New team member?{" "}
        <Link to="/signup" className="font-semibold text-sky-600 hover:underline" data-testid="goto-signup-link">Create an account</Link>
      </p>
    </AuthShell>
  );
}
